import React, { useContext, useEffect, useState } from 'react'
import { Link } from '@/utils'
import { Menu, Sun, Moon, Store } from 'lucide-react'
import UserPanel from '@/components/Shared/UserPanel'
import Button from 'devextreme-react/button'
import { Logo } from '@/components/Icons'
import { MenuCtx } from '@/components/Menu'
import { usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AppCtx } from '@/components/Providers/AppProvider.jsx'
import Globalize from 'globalize'
import mkMessages from '@/lang/mk.json'
import supplementalCldrData from 'devextreme-cldr-data/supplemental.json'
import mkCldrData from 'devextreme-cldr-data/mk.json'
import { buttonLink } from '@/constants'
import { renderToStaticMarkup } from 'react-dom/server'

const Header = ({ menuToggleEnabled }) => {
  const { t, i18n } = useTranslation()
  const { toggleMenu } = useContext(MenuCtx)
  const { setLanguage } = useContext(AppCtx)
  const { company, app, url } = usePage().props

  useEffect(() => {
    if (i18n.language !== app.locale) {
      i18n.changeLanguage(app.locale)
      setLanguage(app.locale)

      if (app.locale === 'mk') {
        Globalize.load(mkCldrData, supplementalCldrData)
        Globalize.loadMessages(mkMessages)
      }
      Globalize.locale(app.locale)
    }
  }, [app.locale])

  const storedDarkMode = () => {
    if (typeof window !== 'undefined') {
      return JSON.parse(window.localStorage.getItem('DARK_MODE')) ||
        (!('DARK_MODE' in window.localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
    }

    return false
  }

  const [darkMode, setDarkMode] = useState(storedDarkMode)
  const toggleDarkMode = () => setDarkMode(!darkMode)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('DARK_MODE', JSON.stringify(darkMode))
    }

    if (darkMode) {
      document.querySelector('html').classList.add('dark')
      document.querySelector('body').classList.add('dx-swatch-moshop-dark')

      return
    }

    document.querySelector('html').classList.remove('dark')
    document.querySelector('body').classList.remove('dx-swatch-moshop-dark')
  }, [darkMode])

  return (
    <header
      className='main-header sticky top-0 z-40 w-full h-14 flex flex-col items-center border-b dx-color-border dx-component-color-bg'
    >
      <div className='mx-auto flex w-full flex-1 items-center justify-between px-4 py-2'>
        <div className='flex items-center space-x-4'>
          <div className='flex items-center space-x-4'>
            {menuToggleEnabled &&
              <Button
                className='menu-button'
                stylingMode='outlined'
                hint={t('ui.menu')}
                onClick={toggleMenu}
                elementAttr={{
                  'aria-label': t('ui.menu')
                }}
              >
                <Menu size={20} />
              </Button>}

            <Link href='/' className='flex-none'>

              <div className={`flex items-center justify-center ${company && company.logo ? 'h-8 px-4 py-1 bg-white rounded' : ''}`}>
                {company && company.logo &&
                  <img
                    src={company.logo.extra_small}
                    alt={company?.name}
                    className='mx-auto max-h-full max-w-16 max-[350px]:max-w-12 sm:max-w-none'
                  />}

                {!company.logo &&
                  <>
                    <span className='sr-only'>moshop</span>
                    <Logo className='mx-auto h-6 max-w-26 sm:max-w-none' />
                  </>}
              </div>

            </Link>
          </div>
        </div>
        <nav className='flex items-center' />

        <div>
          <Button
            className='store-panel-button mr-1'
            stylingMode='outlined'
            hint={t('labels.view_online_store')}
            onClick={() => {
              window.open(url.public, '_blank')
            }}
            icon={renderToStaticMarkup(<Store size={20} />)}
            {...buttonLink.renderLink(url.public)}
          />

          <Button
            className='theme-panel-button sm:mr-1'
            stylingMode='outlined'
            hint={t('labels.theme')}
            onClick={toggleDarkMode}
          >
            <span className='sr-only'>
              <span className='dark:hidden'>Switch to dark theme</span>
              <span className='hidden dark:inline'>Switch to light theme</span>
            </span>

            <Sun size={20} className='hidden dark:inline' />
            <Moon size={20} className='dark:hidden' />
          </Button>

          <Button
            className='user-panel-button'
            width='auto'
            height='100%'
            stylingMode='text'
            hint={t('labels.user_menu')}
            elementAttr={{
              'aria-label': t('labels.user_menu')
            }}
          >
            <UserPanel menuMode='context' />
          </Button>
        </div>
      </div>
    </header>
  )
}

export default Header
